import { canOpenResource } from '../../access';
import { DomainFault as BusinessError } from '../../contracts';
import { policyAllows } from '../../modules';
import type { BusinessService } from '../../service';
import {
  type Entity,
  type SessionIdentity,
  type Snapshot,
  seedUuid,
} from '../../types';
import { bindingAt, capabilities } from '../device';
import { container, MAX_BYTES, MAX_FILES, sampleWave, sha256 } from './audio';
import {
  type Association,
  activeStages,
  batchFiles,
  type CollectionCommand,
  type CollectionData,
  type CollectionMeta,
  type CollectionSnapshot,
  type ImportFile,
  type RecordingSourceConfirmed,
  type SourceFile,
  stationBytes,
  unfinished,
  visibleCampus,
} from './types';

const fail = (code: string, message: string): never => {
  throw new BusinessError(code, message);
};
export function collectionData(state: Snapshot): CollectionData | undefined {
  return (state as CollectionSnapshot).collectionV1;
}
export function canCollect(state: Snapshot, write = false) {
  return (
    canOpenResource('collection', state) &&
    policyAllows(state, 'batch', write ? 'write' : 'read') &&
    (!write ||
      ['groupOperator', 'campusManager', 'deviceManager'].includes(
        state.context.role,
      ))
  );
}
export function hasCollectionWork(state: Snapshot | undefined) {
  if (!state || !canCollect(state, true)) return false;
  const data = collectionData(state);
  return !!data?.stations.some(
    (st) =>
      st.enabled &&
      st.online &&
      visibleCampus(st.campus, state) &&
      data.batches.some(
        (b) =>
          b.stationUuid === st.uuid &&
          data.files.some(
            (f) => f.batchUuid === b.uuid && activeStages.includes(f.stage),
          ),
      ),
  );
}
export function collectionCapabilities(state: Snapshot, device: Entity) {
  const model = state.entities.find(
    (e) => e.kind === 'model' && e.title === device.model,
  );
  const values = capabilities(model);
  return {
    readable: values.录音读取 === '已验证',
    network: values.远程控制 === '已验证',
    version:
      model?.fields?.publishedVersion || `legacy:${model?.uuid || 'unknown'}:1`,
    values,
  };
}
/** Minimal association directory; never returns teacher profiles or recording content. */
export function collectionPeople(state: Snapshot) {
  if (!canCollect(state)) return [];
  return state.entities
    .filter(
      (e) =>
        e.kind === 'teacher' &&
        e.status === 'active' &&
        visibleCampus(e.campus, state),
    )
    .map(({ memberUuid, title, campus }) => ({ memberUuid, title, campus }));
}
export function initialCollection(state: Snapshot): CollectionData {
  return {
    version: 1,
    revision: 1,
    batches: [],
    items: [],
    files: [],
    imports: [],
    events: [],
    receipts: {},
    lastTick: 0,
    stations: state.entities
      .filter((e) => e.kind === 'station')
      .map((e) => ({
        uuid: e.uuid,
        name: e.title,
        campus: e.campus,
        enabled:
          e.fields?.collectionEnabled !== undefined
            ? e.fields.collectionEnabled === 'true'
            : e.status === 'active',
        online: e.fields?.collectionOnline !== 'false',
        methods: ['usb', 'network'],
        capacityBytes: 512 * 1024 * 1024,
        revision: 1,
      })),
  };
}
export function projectCollection(state: Snapshot): CollectionData | undefined {
  if (!canCollect(state)) return undefined;
  const all = collectionData(state);
  if (!all) return undefined;
  const stations = all.stations.filter((s) => visibleCampus(s.campus, state));
  const batches = all.batches.filter((b) => visibleCampus(b.campus, state));
  for (const row of state.entities.filter(
    (row) =>
      row.kind === 'batch' &&
      visibleCampus(row.campus, state) &&
      !all.batches.some((batch) => batch.uuid === row.uuid),
  )) {
    batches.push({
      uuid: row.uuid,
      title: row.title,
      campus: row.campus,
      stationUuid:
        row.fields?.stationUuid ||
        stations.find((station) => station.campus === row.campus)?.uuid ||
        '',
      itemUuids: [],
      createdAt: row.createdAt,
      revision: row.revision,
      legacySummary: {
        status: row.status,
        progress: row.progress || 0,
        note: row.note,
      },
    });
  }
  const batchIds = new Set(batches.map((b) => b.uuid));
  const files = all.files.filter((f) => batchIds.has(f.batchUuid));
  const imports = all.imports.filter((f) =>
    visibleCampus(f.association.campus, state),
  );
  const importIds = new Set(imports.map((f) => f.uuid));
  return {
    ...all,
    stations,
    batches,
    files,
    imports,
    items: all.items.filter((i) => batchIds.has(i.batchUuid)),
    events: all.events.filter(
      (e) => !!e.batchUuid && batchIds.has(e.batchUuid),
    ),
    preview:
      all.preview &&
      all.preview.contextKey === contextKey(state) &&
      all.preview.fileUuids.every((id) => importIds.has(id))
        ? all.preview
        : undefined,
    receipts: {},
  };
}
function contextKey(s: Snapshot) {
  return JSON.stringify(s.context);
}
function event(
  s: Snapshot,
  d: CollectionData,
  identity: SessionIdentity,
  text: string,
  file?: SourceFile,
  batchUuid?: string,
) {
  const uuid = crypto.randomUUID();
  const batch = d.batches.find(
    (batch) => batch.uuid === (file?.batchUuid || batchUuid),
  );
  const stationUuid =
    batch?.stationUuid ||
    d.stations.find((station) => station.campus === file?.association.campus)
      ?.uuid;
  d.events.unshift({
    uuid,
    text,
    at: s.now,
    actorMemberUuid: identity.memberUuid,
    fileUuid: file?.uuid,
    batchUuid: file?.batchUuid || batchUuid,
  });
  const entity: Entity = {
    uuid,
    kind: 'audit',
    title: text,
    campus:
      file?.association.campus ||
      d.batches.find((b) => b.uuid === batchUuid)?.campus ||
      s.context.campus,
    memberUuid: identity.memberUuid,
    status: 'completed',
    createdAt: s.now,
    updatedAt: s.now,
    revision: 1,
    note: text,
    sourceUuid:
      stationUuid ||
      (s.entities.some((row) => row.uuid === batchUuid)
        ? batchUuid
        : undefined),
    fields: {
      actorMemberUuid: identity.memberUuid,
      realOccurredAt: new Date().toISOString(),
      domain: 'collection',
      sourceFileUuid: file?.uuid || '',
      collectionBatchUuid: file?.batchUuid || batchUuid || '',
    },
  };
  s.entities.push(entity);
}
function associationValid(s: Snapshot, a: Association) {
  if (
    !a.title.trim() ||
    a.title.length > 100 ||
    !a.campus ||
    !a.confirmed ||
    !['classroom', 'communication', 'other'].includes(a.scene)
  )
    fail('VALIDATION', '请确认标题、校区、场景与业务关联');
  if (!visibleCampus(a.campus, s)) fail('FORBIDDEN', '没有该校区的操作权限');
  if (
    !Number.isFinite(Date.parse(a.startedAt)) ||
    Date.parse(a.startedAt) > Date.parse(s.now)
  )
    fail('VALIDATION', '发生时间无效或晚于当前业务时间');
  if (
    !s.entities.some(
      (e) =>
        e.kind === 'teacher' &&
        e.memberUuid === a.teacherMemberUuid &&
        e.campus === a.campus,
    )
  )
    fail('VALIDATION', '请选择该校区的教师');
  if (
    a.lessonUuid &&
    !s.entities.some(
      (e) =>
        e.kind === 'lesson' && e.uuid === a.lessonUuid && e.campus === a.campus,
    )
  )
    fail('VALIDATION', '课程关联无效');
}
function validateAudio(f: ImportFile) {
  if (
    f.error ||
    !f.blob ||
    f.blob.size !== f.sizeBytes ||
    f.sizeBytes <= 0 ||
    f.sizeBytes > MAX_BYTES ||
    !Number.isFinite(f.durationMs) ||
    f.durationMs <= 0 ||
    !/^[0-9a-f]{64}$/.test(f.sha256)
  )
    fail('VALIDATION', '文件未通过内容校验，请重新选择');
}
/** In-transaction recording consumer compatible with the existing recording archive. */
export function confirmSource(
  s: Snapshot,
  d: CollectionData,
  f: SourceFile,
): RecordingSourceConfirmed {
  if (f.confirmedAt && f.recordingUuid)
    return {
      sourceFileUuid: f.uuid,
      recordingUuid: f.recordingUuid,
      confirmedAt: f.confirmedAt,
      result: f.duplicateOfFileUuid ? 'duplicate' : 'created',
      duplicateOfFileUuid: f.duplicateOfFileUuid,
    };
  if (
    f.stage !== 'confirming' ||
    f.transferredBytes !== f.sizeBytes ||
    f.integrity === 'corrupt' ||
    f.integrity === 'pending'
  )
    fail('INVALID_TRANSITION', '文件尚未传输或校验完成');
  associationValid(s, f.association);
  if (!policyAllows(s, 'recording', 'write'))
    fail('FORBIDDEN', '当前策略禁止创建或确认录音来源');
  const source = d.files.find(
    (v) =>
      v.uuid !== f.uuid &&
      v.sha256 === f.sha256 &&
      v.confirmedAt &&
      !v.duplicateOfFileUuid,
  );
  const existing = s.entities.find(
    (e) => e.kind === 'recording' && e.hash === f.sha256,
  );
  const recordingUuid = existing?.uuid || crypto.randomUUID();
  if (!existing) {
    if (!f.blob) fail('VALIDATION', '源文件不可用，请重新读取');
    const duration = f.durationMs;
    s.entities.push({
      uuid: recordingUuid,
      kind: 'recording',
      title: f.association.title,
      campus: f.association.campus,
      memberUuid: f.association.teacherMemberUuid,
      deviceUuid: f.deviceUuid,
      status: 'ready',
      createdAt: s.now,
      updatedAt: s.now,
      revision: 1,
      note: '模拟转写，非实际识别结果',
      startAt: f.association.startedAt,
      endAt: new Date(
        Date.parse(f.association.startedAt) + duration,
      ).toISOString(),
      hash: f.sha256,
      audioBlob: f.blob,
      sourceUuid: f.uuid,
      fields: {
        imported: 'true',
        filename: f.filename,
        durationMs: String(duration),
        scene: f.association.scene,
        lessonUuid: f.association.lessonUuid || '',
        linkStatus: f.association.confirmed ? 'confirmed' : 'pending',
        collectionBatchUuid: f.batchUuid,
        sourceFileUuid: f.uuid,
        bindingSnapshot: JSON.stringify(f.bindingSnapshot || null),
      },
      versions: [
        {
          uuid: crypto.randomUUID(),
          createdAt: s.now,
          segments: [
            {
              uuid: crypto.randomUUID(),
              startMs: 0,
              endMs: duration,
              speaker: '说话人（未确认）',
              text: '本录音未执行真实识别。此处为模拟占位文本，不代表音频实际内容。',
            },
          ],
        },
      ],
    });
  }
  f.recordingUuid = recordingUuid;
  f.confirmedAt = s.now;
  f.stage = 'completed';
  f.duplicateOfFileUuid =
    source?.uuid ||
    (existing ? existing.sourceUuid || existing.uuid : undefined);
  f.integrity = existing ? 'duplicate' : 'valid';
  f.reason = undefined;
  f.failure = undefined;
  if (!existing)
    s.entities.push({
      uuid: crypto.randomUUID(),
      kind: 'message',
      title: '录音已完成后台确认',
      campus: f.association.campus,
      memberUuid: f.association.teacherMemberUuid,
      sourceUuid: recordingUuid,
      status: 'unread',
      createdAt: s.now,
      updatedAt: s.now,
      revision: 1,
      note: f.association.title,
      fields: {
        target: `/recording/${recordingUuid}`,
        collectionSuccessBytes: String(f.sizeBytes),
        usageKey: `collection:${f.sha256}`,
      },
    });
  return {
    sourceFileUuid: f.uuid,
    recordingUuid,
    confirmedAt: s.now,
    result: existing ? 'duplicate' : 'created',
    duplicateOfFileUuid: f.duplicateOfFileUuid,
  };
}
function getFile(s: Snapshot, d: CollectionData, id: string) {
  const file = d.files.find((f) => f.uuid === id);
  if (!file || !visibleCampus(file.association.campus, s))
    return fail('FORBIDDEN', '文件不存在或无权访问');
  return file;
}
function station(
  s: Snapshot,
  d: CollectionData,
  id: string,
  available = false,
) {
  const value = d.stations.find((v) => v.uuid === id);
  if (!value || !visibleCampus(value.campus, s))
    return fail('FORBIDDEN', '站点不存在或无权访问');
  if (available && (!value.enabled || !value.online))
    fail('CAPABILITY_UNAVAILABLE', '站点已停用或离线，无法执行采集命令');
  return value;
}
function restoreCheckpoint(d: CollectionData, f: SourceFile) {
  if (
    !f.deviceUuid ||
    f.transferredBytes === 0 ||
    f.transferredBytes === f.sizeBytes
  )
    return;
  const item = d.items.find((item) => item.uuid === f.queueItemUuid);
  let snapshot: { values?: Record<string, string> } | undefined;
  try {
    snapshot = item ? JSON.parse(item.capability) : undefined;
  } catch {
    snapshot = undefined;
  }
  if (snapshot?.values?.补传 !== '已验证') {
    f.transferredBytes = 0;
    f.previousStage = 'pending';
    f.integrity = 'pending';
  }
}
export function reduceCollection(
  current: Snapshot,
  input: CollectionCommand,
  meta: CollectionMeta,
  identity: SessionIdentity,
): { state: Snapshot; uuid: string } {
  if (!identity.memberUuid || !identity.tenantUuid)
    fail('UNAUTHENTICATED', '缺少真实会话');
  if (!canCollect(current, true))
    fail('FORBIDDEN', '当前业务视角无采集操作权限');
  const s: CollectionSnapshot = structuredClone(current);
  s.collectionV1 ??= initialCollection(s);
  const d = s.collectionV1;
  if (d.version !== 1) fail('CONFLICT', '采集数据版本不兼容，请联系管理员');
  if (d.receipts[meta.commandUuid])
    return { state: current, uuid: d.receipts[meta.commandUuid] };
  if (
    meta.expectedRevision !== undefined &&
    d.revision !== meta.expectedRevision
  )
    fail('CONFLICT', '数据已变化，请刷新后重新预览');
  let result = '';
  switch (input.type) {
    case 'legacyControl':
      return fail('INVALID_TRANSITION', '历史批次控制须通过共享服务执行');
    case 'initialize':
      if (collectionData(current)) return { state: current, uuid: '' };
      break;
    case 'enqueue': {
      const st = station(s, d, input.stationUuid, true);
      if (!st.methods.includes(input.method))
        fail('CAPABILITY_UNAVAILABLE', '站点不支持此连接方式');
      const deviceIds = [...new Set(input.deviceUuids)];
      if (
        !deviceIds.length ||
        deviceIds.length > 20 ||
        input.samples.length !== deviceIds.length
      )
        fail('VALIDATION', '请选择1至20台设备');
      const batchUuid = crypto.randomUUID();
      const batch = {
        uuid: batchUuid,
        title: `采集批次 ${d.batches.length + 1}`,
        stationUuid: st.uuid,
        campus: st.campus,
        itemUuids: [] as string[],
        createdAt: s.now,
        revision: 1,
      };
      deviceIds.forEach((id, index) => {
        const device = s.entities.find(
          (e) => e.kind === 'device' && e.uuid === id,
        );
        if (
          !device ||
          !visibleCampus(device.campus, s) ||
          device.campus !== st.campus
        )
          fail('FORBIDDEN', '设备与站点校区不一致');
        const capability = collectionCapabilities(s, device!);
        if (
          !capability.readable ||
          ['disabled', 'retired', 'repair'].includes(device!.status) ||
          (input.method === 'network' &&
            (!device!.supportsRemote || !capability.network))
        )
          fail('CAPABILITY_UNAVAILABLE', '设备能力未验证或不支持此连接方式');
        if (
          s.entities.some(
            (e) =>
              e.kind === 'batch' &&
              e.deviceUuid === id &&
              !['completed', 'cancelled'].includes(e.status),
          ) ||
          d.items.some(
            (i) =>
              i.deviceUuid === id &&
              i.fileUuids.some((fid) =>
                d.files.some((f) => f.uuid === fid && unfinished(f)),
              ),
          )
        )
          fail('CONFLICT', '设备已有未结束的采集，请先处理原队列');
        const sample = input.samples[index];
        validateAudio(sample);
        const itemUuid = crypto.randomUUID();
        const fileUuid = crypto.randomUUID();
        const binding = bindingAt(
          s.entities.filter((e) => e.kind === 'binding'),
          id,
          sample.association.startedAt,
        );
        const association: Association = {
          ...sample.association,
          campus: st.campus,
          teacherMemberUuid: binding?.memberUuid || '',
          confirmed: !!binding,
        };
        d.items.push({
          uuid: itemUuid,
          batchUuid,
          deviceUuid: id,
          deviceName: device!.title,
          method: input.method,
          capability: JSON.stringify({
            model: device!.model,
            version: capability.version,
            values: capability.values,
          }),
          fileUuids: [fileUuid],
          generation: 1,
        });
        d.files.push({
          uuid: fileUuid,
          batchUuid,
          queueItemUuid: itemUuid,
          deviceUuid: id,
          sourceFileKey: `${id}:generated-pcm-v1`,
          filename: sample.filename,
          sizeBytes: sample.sizeBytes,
          durationMs: sample.durationMs,
          sha256: sample.sha256,
          blob: sample.blob,
          integrity: 'pending',
          stage: 'pending',
          transferredBytes: 0,
          association,
          retries: 0,
          failOnce:
            input.interruptSecond && index === 1 ? 'connection' : undefined,
          bindingSnapshot: binding
            ? {
                uuid: binding.uuid,
                memberUuid: binding.memberUuid,
                campus: binding.campus,
                effectiveFrom: binding.startAt!,
                effectiveTo: binding.endAt,
              }
            : undefined,
        });
        batch.itemUuids.push(itemUuid);
      });
      d.batches.unshift(batch);
      if (stationBytes(d, st.uuid) > st.capacityBytes)
        fail('QUOTA_EXCEEDED', '采集站缓存容量不足');
      result = batchUuid;
      event(
        s,
        d,
        identity,
        '设备已加入采集队列；源文件保留，连接与传输采用模拟',
        undefined,
        batchUuid,
      );
      break;
    }
    case 'control': {
      if (!input.fileUuids.length) fail('VALIDATION', '没有可操作文件');
      input.fileUuids.forEach((id) => {
        const f = getFile(s, d, id);
        if (input.action === 'pause') {
          if (!activeStages.includes(f.stage))
            fail('INVALID_TRANSITION', '只能暂停活动文件');
          f.previousStage = f.stage;
          f.stage = 'paused';
        } else if (input.action === 'resume') {
          if (f.stage !== 'paused') fail('INVALID_TRANSITION', '文件没有暂停');
          station(
            s,
            d,
            d.batches.find((b) => b.uuid === f.batchUuid)!.stationUuid,
            true,
          );
          restoreCheckpoint(d, f);
          f.stage = f.previousStage || 'pending';
        } else if (input.action === 'retry') {
          if (
            f.stage !== 'failed' ||
            f.reason === 'corrupt' ||
            f.reason === 'association' ||
            f.reason === 'time'
          )
            fail('INVALID_TRANSITION', '请先处理文件损坏、归属或时间问题');
          station(
            s,
            d,
            d.batches.find((b) => b.uuid === f.batchUuid)!.stationUuid,
            true,
          );
          restoreCheckpoint(d, f);
          f.stage =
            f.transferredBytes === f.sizeBytes ? 'confirming' : 'pending';
          f.retries++;
          f.failure = undefined;
          f.reason = undefined;
        } else {
          if (!unfinished(f))
            fail('INVALID_TRANSITION', '已确认或已取消的文件不能取消');
          f.stage = 'cancelled';
          const item = d.items.find((i) => i.uuid === f.queueItemUuid);
          if (item) item.generation++;
        }
        event(
          s,
          d,
          identity,
          {
            pause: '暂停文件并保存检查点',
            resume: '从检查点继续',
            retry: '重试未确认文件',
            cancel: '取消未确认文件，源文件保留',
          }[input.action],
          f,
        );
      });
      break;
    }
    case 'reorder': {
      const item = d.items.find((i) => i.uuid === input.itemUuid);
      if (!item) fail('NOT_FOUND', '队列项不存在');
      const batch = d.batches.find((b) => b.uuid === item!.batchUuid)!;
      station(s, d, batch.stationUuid);
      if (
        !item!.fileUuids.every(
          (id) => d.files.find((f) => f.uuid === id)?.stage === 'pending',
        )
      )
        fail('INVALID_TRANSITION', '仅待开始设备可排序');
      const index = batch.itemUuids.indexOf(item!.uuid);
      const target = index + input.direction;
      if (target < 0 || target >= batch.itemUuids.length)
        fail('VALIDATION', '已到队列边界');
      const other = d.items.find((i) => i.uuid === batch.itemUuids[target])!;
      if (
        !other.fileUuids.every(
          (id) => d.files.find((f) => f.uuid === id)?.stage === 'pending',
        )
      )
        fail('INVALID_TRANSITION', '不能跨越已运行设备');
      [batch.itemUuids[index], batch.itemUuids[target]] = [
        batch.itemUuids[target],
        batch.itemUuids[index],
      ];
      batch.revision++;
      event(s, d, identity, '调整待采设备优先级', undefined, batch.uuid);
      break;
    }
    case 'station': {
      const value = input.station;
      if (!policyAllows(s, 'station', 'write'))
        fail('FORBIDDEN', '当前策略禁止修改采集站');
      if (
        !visibleCampus(value.campus, s) ||
        !s.entities.some(
          (e) => e.kind === 'station' && e.campus === value.campus,
        )
      )
        fail('FORBIDDEN', '校区无效或无权操作');
      if (
        !value.name.trim() ||
        value.name.trim().length > 40 ||
        !value.methods.length ||
        value.methods.some((m) => !['usb', 'network'].includes(m)) ||
        !Number.isFinite(value.capacityBytes) ||
        value.capacityBytes < MAX_BYTES
      )
        fail('VALIDATION', '请填写有效站点名称、连接方式和容量（至少50MB）');
      const existing = d.stations.find((st) => st.uuid === value.uuid);
      if (existing && existing.revision !== value.revision)
        fail('CONFLICT', '站点配置已变化');
      if (existing && existing.campus !== value.campus)
        fail('VALIDATION', '已有站点不能更改校区');
      if (
        d.stations.some(
          (st) =>
            st.uuid !== value.uuid &&
            st.campus === value.campus &&
            st.name.trim().toLocaleLowerCase() ===
              value.name.trim().toLocaleLowerCase(),
        )
      )
        fail('CONFLICT', '同校区站点名称已存在');
      const ids = new Set(
        d.batches
          .filter((b) => b.stationUuid === value.uuid)
          .map((b) => b.uuid),
      );
      if (
        s.entities.some(
          (e) =>
            e.kind === 'batch' &&
            e.campus === value.campus &&
            (e.fields?.stationUuid === value.uuid || !e.fields?.stationUuid) &&
            activeStages.some((stage) => stage === e.status),
        ) ||
        d.files.some(
          (f) => ids.has(f.batchUuid) && activeStages.includes(f.stage),
        )
      )
        fail('INVALID_TRANSITION', '请先暂停站点的活动任务，再修改配置');
      if (stationBytes(d, value.uuid) > value.capacityBytes)
        fail('QUOTA_EXCEEDED', '容量不能低于已排队文件大小');
      const next = {
        ...value,
        name: value.name.trim(),
        revision: (existing?.revision || 0) + 1,
      };
      if (existing) Object.assign(existing, next);
      else d.stations.push(next);
      const row = s.entities.find(
        (e) => e.kind === 'station' && e.uuid === next.uuid,
      );
      const fields = {
        ...row?.fields,
        collectionEnabled: String(next.enabled),
        collectionOnline: String(next.online),
        capacityBytes: String(next.capacityBytes),
        methods: next.methods.join(','),
      };
      if (row)
        Object.assign(row, {
          title: next.name,
          status: next.enabled && next.online ? 'active' : 'disabled',
          fields,
          updatedAt: s.now,
          revision: row.revision + 1,
        });
      else
        s.entities.push({
          uuid: next.uuid,
          kind: 'station',
          title: next.name,
          campus: next.campus,
          memberUuid: identity.memberUuid,
          status: next.enabled && next.online ? 'active' : 'disabled',
          createdAt: s.now,
          updatedAt: s.now,
          revision: 1,
          note: '',
          fields,
        });
      event(
        s,
        d,
        identity,
        `更新采集站：${next.name} · ${next.enabled ? '启用' : '停用'}`,
      );
      break;
    }
    case 'reassign': {
      const item = d.items.find((i) => i.uuid === input.itemUuid);
      if (!item) fail('NOT_FOUND', '队列项不存在');
      const batch = d.batches.find((b) => b.uuid === item!.batchUuid)!;
      const from = station(s, d, batch.stationUuid);
      const target = station(s, d, input.stationUuid, true);
      if (
        from.uuid === target.uuid ||
        from.campus !== target.campus ||
        !target.methods.includes(item!.method)
      )
        fail('CAPABILITY_UNAVAILABLE', '目标站需同校区且支持当前协议/连接方式');
      const device = s.entities.find(
        (e) => e.kind === 'device' && e.uuid === item!.deviceUuid,
      );
      if (!device || ['disabled', 'retired', 'repair'].includes(device.status))
        fail('CAPABILITY_UNAVAILABLE', '设备已停用、报废或维修，不能转移采集');
      const capability = collectionCapabilities(s, device!);
      if (
        !capability.readable ||
        (item!.method === 'network' && !capability.network)
      )
        fail('CAPABILITY_UNAVAILABLE', '当前设备能力不支持转移后的连接方式');
      const files = item!.fileUuids.map((id) => getFile(s, d, id));
      if (!files.every((f) => f.stage === 'pending' || f.stage === 'paused'))
        fail('INVALID_TRANSITION', '请先暂停任务再重分配');
      if (
        stationBytes(d, target.uuid) +
          files.reduce((n, f) => n + f.sizeBytes, 0) >
        target.capacityBytes
      )
        fail('QUOTA_EXCEEDED', '目标站缓存不足');
      const nextBatch = {
        ...batch,
        uuid: crypto.randomUUID(),
        title: `${batch.title} · 转移`,
        stationUuid: target.uuid,
        itemUuids: [item!.uuid],
        revision: 1,
      };
      batch.itemUuids = batch.itemUuids.filter((id) => id !== item!.uuid);
      batch.revision++;
      item!.batchUuid = nextBatch.uuid;
      files.forEach((f) => {
        f.batchUuid = nextBatch.uuid;
      });
      d.batches.unshift(nextBatch);
      result = nextBatch.uuid;
      event(
        s,
        d,
        identity,
        `队列已转移至${target.name}；暂停文件仍需人工继续`,
        undefined,
        nextBatch.uuid,
      );
      break;
    }
    case 'saveImports': {
      const pending = d.imports.filter(
        (f) => !['confirmed', 'duplicate'].includes(f.status),
      );
      if (pending.length + input.files.length > MAX_FILES)
        fail('VALIDATION', '当前草稿最多20个文件，请先处理或移除已有文件');
      input.files.forEach((f) => {
        if (!visibleCampus(f.association.campus, s))
          fail('FORBIDDEN', '没有该校区的操作权限');
        if (d.imports.some((i) => i.uuid === f.uuid))
          fail('CONFLICT', '该文件已保存');
        if (f.status === 'ready') validateAudio(f);
        d.imports.push(f);
      });
      d.preview = undefined;
      break;
    }
    case 'replaceImport': {
      const index = d.imports.findIndex((file) => file.uuid === input.fileUuid);
      const existing = d.imports[index];
      if (!existing || !visibleCampus(existing.association.campus, s))
        fail('FORBIDDEN', '文件不存在或无权访问');
      if (['confirmed', 'duplicate'].includes(existing.status))
        fail('INVALID_TRANSITION', '已确认来源不能被替换');
      if (!visibleCampus(input.file.association.campus, s))
        fail('FORBIDDEN', '没有目标校区的操作权限');
      if (input.file.status === 'ready') validateAudio(input.file);
      d.imports[index] = {
        ...input.file,
        uuid: existing.uuid,
        association: { ...input.file.association, confirmed: false },
      };
      d.preview = undefined;
      break;
    }
    case 'associate': {
      const f = d.imports.find((f) => f.uuid === input.fileUuid);
      if (!f || !visibleCampus(f.association.campus, s))
        fail('FORBIDDEN', '文件不存在或无权访问');
      if (['confirmed', 'duplicate'].includes(f!.status))
        fail('INVALID_TRANSITION', '已确认来源不能修改');
      associationValid(s, input.association);
      f!.association = input.association;
      d.preview = undefined;
      break;
    }
    case 'removeImport': {
      const f = d.imports.find((f) => f.uuid === input.fileUuid);
      if (!f || !visibleCampus(f.association.campus, s))
        fail('FORBIDDEN', '文件不存在或无权访问');
      if (['confirmed', 'duplicate'].includes(f!.status))
        fail('INVALID_TRANSITION', '已确认来源需保留追溯');
      d.imports = d.imports.filter((f) => f.uuid !== input.fileUuid);
      d.preview = undefined;
      break;
    }
    case 'previewImport': {
      const ids = [...new Set(input.fileUuids)];
      if (!ids.length || ids.length > MAX_FILES)
        fail('VALIDATION', '请选择合法文件');
      ids.forEach((id) => {
        const f = d.imports.find((f) => f.uuid === id);
        if (!f || !visibleCampus(f.association.campus, s))
          fail('FORBIDDEN', '文件不存在或无权访问');
        if (!['ready', 'failed'].includes(f!.status))
          fail('INVALID_TRANSITION', '只能导入未确认的合法文件');
        validateAudio(f!);
        associationValid(s, f!.association);
      });
      result = crypto.randomUUID();
      d.preview = {
        uuid: result,
        fileUuids: ids,
        expiresAt: Date.now() + 15 * 60 * 1000,
        revision: d.revision + 1,
        contextKey: contextKey(s),
      };
      break;
    }
    case 'confirmImport': {
      const p = d.preview;
      if (
        !p ||
        p.uuid !== input.previewUuid ||
        p.expiresAt < Date.now() ||
        p.contextKey !== contextKey(s)
      )
        fail('CONFLICT', '导入预览已过期或数据已变化，请重新预览');
      const batchUuid = crypto.randomUUID();
      const first = d.imports.find((f) => f.uuid === p!.fileUuids[0])!;
      d.batches.unshift({
        uuid: batchUuid,
        title: `手动导入 ${d.batches.length + 1}`,
        campus: first.association.campus,
        stationUuid: '',
        itemUuids: [],
        createdAt: s.now,
        revision: 1,
      });
      p!.fileUuids.forEach((id) => {
        const f = d.imports.find((f) => f.uuid === id)!;
        validateAudio(f);
        associationValid(s, f.association);
        if (f.association.campus !== first.association.campus)
          fail('VALIDATION', '请按校区分批确认');
        const source: SourceFile = {
          uuid: crypto.randomUUID(),
          batchUuid,
          queueItemUuid: '',
          sourceFileKey: f.uuid,
          filename: f.filename,
          sizeBytes: f.sizeBytes,
          durationMs: f.durationMs,
          sha256: f.sha256,
          blob: f.blob,
          integrity: 'valid',
          stage: 'confirming',
          transferredBytes: f.sizeBytes,
          uploadedAt: s.now,
          association: f.association,
          retries: 0,
        };
        d.files.push(source);
        const receipt = confirmSource(s, d, source);
        f.status = receipt.result === 'duplicate' ? 'duplicate' : 'confirmed';
        f.sourceFileUuid = source.uuid;
        f.recordingUuid = receipt.recordingUuid;
        event(
          s,
          d,
          identity,
          receipt.result === 'duplicate'
            ? '重复内容已确认，保留来源关系，未新增录音'
            : '导入已完成后台确认，创建可归档录音',
          source,
        );
      });
      d.preview = undefined;
      result = batchUuid;
      break;
    }
    case 'resolve': {
      const f = getFile(s, d, input.fileUuid);
      if (!input.note.trim()) fail('VALIDATION', '请填写人工处理说明');
      if (f.stage !== 'failed')
        fail('INVALID_TRANSITION', '异常已被处理，请刷新');
      if (input.action === 'association') {
        if (
          !['association', 'time'].includes(f.reason || '') ||
          !input.association
        )
          fail('INVALID_TRANSITION', '此异常不能通过补充关联处理');
        associationValid(s, { ...input.association!, confirmed: true });
        f.association = { ...input.association!, confirmed: false };
        // Explicit human association confirmation is performed in the second resolve step.
        f.failure = '资料已补充，请核实后确认关联';
        f.reason = 'association';
      } else if (input.action === 'confirmAssociation') {
        if (!f.association.teacherMemberUuid)
          fail('VALIDATION', '请先补充教师归属');
        if (f.reason !== 'association' || !f.failure?.includes('资料已补充'))
          fail('INVALID_TRANSITION', '请先补充归属，再确认关联');
        associationValid(s, { ...f.association, confirmed: true });
        f.association.confirmed = true;
        f.stage = f.transferredBytes === f.sizeBytes ? 'confirming' : 'pending';
        f.reason = undefined;
        f.failure = undefined;
      } else if (input.action === 'duplicate') {
        const original = d.files.find(
          (source) =>
            source.uuid !== f.uuid &&
            source.sha256 === f.sha256 &&
            source.confirmedAt,
        );
        if (
          !original ||
          f.transferredBytes !== f.sizeBytes ||
          f.integrity === 'corrupt'
        )
          fail(
            'INVALID_TRANSITION',
            '未找到已确认的相同内容，或文件尚未通过校验',
          );
        f.stage = 'confirming';
        confirmSource(s, d, f);
      } else if (input.action === 'ticket') {
        if (!policyAllows(s, 'ticket', 'write'))
          fail('FORBIDDEN', '当前策略禁止创建工单');
        const existing = s.entities.find(
          (e) =>
            e.kind === 'ticket' &&
            e.sourceUuid === f.uuid &&
            e.status !== 'closed',
        );
        result = existing?.uuid || crypto.randomUUID();
        if (!existing)
          s.entities.push({
            uuid: result,
            kind: 'ticket',
            title: `${f.filename} · 采集异常`,
            campus: f.association.campus,
            memberUuid: identity.memberUuid,
            status: 'open',
            createdAt: s.now,
            updatedAt: s.now,
            revision: 1,
            sourceUuid: f.uuid,
            deviceUuid: f.deviceUuid,
            note: input.note,
          });
      } else {
        if (
          !['connection', 'confirmation', 'capacity'].includes(f.reason || '')
        )
          fail('INVALID_TRANSITION', '此异常需先修复原因，不能直接重试');
        station(
          s,
          d,
          d.batches.find((b) => b.uuid === f.batchUuid)!.stationUuid,
          true,
        );
        f.stage = f.transferredBytes === f.sizeBytes ? 'confirming' : 'pending';
        f.retries++;
        f.failure = undefined;
        f.reason = undefined;
      }
      event(s, d, identity, `异常处置：${input.note.trim()}`, f);
      break;
    }
    case 'tick': {
      if (input.at - d.lastTick < 950) return { state: current, uuid: '' };
      if (
        !d.stations.some(
          (st) =>
            st.enabled &&
            st.online &&
            visibleCampus(st.campus, s) &&
            d.batches.some(
              (b) =>
                b.stationUuid === st.uuid &&
                d.files.some(
                  (f) =>
                    f.batchUuid === b.uuid && activeStages.includes(f.stage),
                ),
            ),
        )
      )
        return { state: current, uuid: '' };
      d.lastTick = input.at;
      // One transfer per station; failures/paused items are skipped. Checkpoints are transactional.
      for (const st of d.stations.filter(
        (st) => st.enabled && st.online && visibleCampus(st.campus, s),
      )) {
        const batches = [...d.batches]
          .reverse()
          .filter((b) => b.stationUuid === st.uuid);
        const ids = batches
          .flatMap((b) => b.itemUuids)
          .flatMap((id) => d.items.find((i) => i.uuid === id)?.fileUuids || []);
        const f = ids
          .map((id) => d.files.find((f) => f.uuid === id)!)
          .find((f) => activeStages.includes(f.stage));
        if (!f) continue;
        if (f.stage === 'pending') f.stage = 'connecting';
        else if (f.stage === 'connecting') f.stage = 'transferring';
        else if (f.stage === 'transferring') {
          if (f.failOnce) {
            f.stage = 'failed';
            f.reason = f.failOnce;
            f.failOnce = undefined;
            f.failure = '模拟连接中断，当前文件检查点已保存；其他设备继续';
          } else {
            f.transferredBytes = Math.min(
              f.sizeBytes,
              f.transferredBytes + Math.ceil(f.sizeBytes / 3),
            );
            if (f.transferredBytes === f.sizeBytes) {
              f.integrity = 'valid';
              f.stage = 'confirming';
              f.uploadedAt = s.now;
            }
          }
        } else {
          try {
            confirmSource(s, d, f);
          } catch (error) {
            f.stage = 'failed';
            f.reason = f.association.confirmed ? 'confirmation' : 'association';
            f.failure = error instanceof Error ? error.message : '后台确认失败';
          }
        }
        event(
          s,
          d,
          identity,
          `${f.filename}：${f.stage === 'completed' ? (f.duplicateOfFileUuid ? '重复内容确认，保留来源关系' : '后台确认完成') : f.failure || f.stage}`,
          f,
        );
      }
      break;
    }
  }
  d.revision++;
  s.revision++;
  if (input.type !== 'tick' && input.type !== 'initialize') {
    result ||= meta.commandUuid;
    d.receipts[meta.commandUuid] = result;
    event(s, d, identity, `采集操作已保存：${input.type}`);
  }
  return { state: s, uuid: result };
}
/** Uses the existing shared repository and publish/subscribe path; no new database or schema. */
export async function collectionCommand(
  service: BusinessService,
  input: CollectionCommand,
  meta: CollectionMeta = { commandUuid: crypto.randomUUID() },
) {
  if (input.type === 'legacyControl') {
    if (!service.state || !canCollect(service.state, true))
      return fail('FORBIDDEN', '无采集操作权限');
    return service.command({
      action: 'queue',
      uuid: input.batchUuid,
      note: input.action,
      meta,
    });
  }
  const incoming =
    input.type === 'saveImports'
      ? input.files
      : input.type === 'replaceImport'
        ? [input.file]
        : input.type === 'enqueue'
          ? input.samples
          : [];
  for (const file of incoming.filter((file) => file.status === 'ready')) {
    validateAudio(file);
    const bytes = await file.blob!.arrayBuffer();
    if (
      (await sha256(bytes)) !== file.sha256 ||
      container(bytes) !== file.filename.split('.').pop()?.toLowerCase()
    )
      fail('VALIDATION', '文件内容摘要或容器不一致，请重新校验');
  }
  let uuid = '';
  const state = await service.repository.change((current) => {
    if (!current) return fail('NOT_READY', '数据尚未初始化');
    const result = reduceCollection(current, input, meta, service.identity);
    uuid = result.uuid;
    return result.state;
  });
  service.publish(state);
  return uuid;
}
export async function simulatedSamples(
  devices: Entity[],
  now: string,
): Promise<ImportFile[]> {
  return Promise.all(
    devices.map(async (device, index) => {
      const seed = Array.from(device.uuid).reduce(
        (n, c, i) => n + c.charCodeAt(0) * (i + 1),
        0,
      );
      const bytes = sampleWave(seed);
      return {
        uuid: seedUuid(93000 + index),
        filename: `${device.title}-模拟采集.wav`,
        sizeBytes: bytes.byteLength,
        durationMs: 2000,
        sha256: await sha256(bytes),
        blob: new Blob([bytes], { type: 'audio/wav' }),
        lastModified: Date.parse(now),
        status: 'ready' as const,
        association: {
          title: `${device.title} · 模拟采集`,
          campus: device.campus,
          teacherMemberUuid: device.memberUuid,
          scene: 'other' as const,
          startedAt: new Date(Date.parse(now) - 2000).toISOString(),
          confirmed: true,
        },
      };
    }),
  );
}
export function confirmedUsage(data: CollectionData) {
  const files = data.files.filter(
    (f) => f.confirmedAt && !f.duplicateOfFileUuid && f.integrity === 'valid',
  );
  return {
    fileCount: files.length,
    sizeBytes: files.reduce((n, f) => n + f.sizeBytes, 0),
    sourceFileUuids: files.map((f) => f.uuid),
  };
}
export { batchFiles };
